import { ProductsPage, productsPlugin } from '@univerus/plugin-products';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TierSelector } from './techdocs/TierSelector';
import { VersionPicker } from './techdocs/VersionPicker';
import { RecentDocsTracker } from './techdocs/RecentDocsTracker';
import { TechDocsReaderRedirect } from './techdocs/TechDocsReaderRedirect';

import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { HomePage } from './components/home/HomePage';
import { GroupsExplorerPage } from './components/groups/GroupsExplorerPage';
import { ReleasesPage } from './components/releases/ReleasesPage';
import { CustomCatalogIndexPage } from './components/catalog/CatalogIndexPage';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { UnifiedThemeProvider } from '@backstage/theme';
import {
  universusLightTheme,
  universusDarkTheme,
} from './themes/universusTheme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';

// Import custom scaffolder field extensions
import {
  UnlinkedEntraGroupPickerFieldExtension,
  TeamInformationFieldsExtension,
  GitHubRepoPickerFieldExtension,
  RepositoryNameFieldsExtension,
  DomainPickerFieldExtension,
} from './scaffolder/fields/extensions';

// Telemetry: Application Insights boot + TechDocs page view tracking
import { AppInsightsBootstrap, TechDocsViewTracker } from './telemetry';

const app = createApp({
  apis,
  themes: [
    {
      id: 'univerus-light',
      title: 'Univerus Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={universusLightTheme} children={children} />
      ),
    },
    {
      id: 'univerus-dark',
      title: 'Univerus Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={universusDarkTheme} children={children} />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(productsPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          {
            id: 'microsoft-auth-provider',
            title: 'Microsoft',
            message: 'Sign in using Microsoft',
            apiRef: microsoftAuthApiRef,
          },
        ]}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/products/*" element={<ProductsPage />} />
    <Route path="/" element={<Navigate to="home" />} />
    <Route path="/home" element={<HomePage />} />
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalogIndexPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={
        <TechDocsReaderRedirect>
          <TechDocsReaderPage />
        </TechDocsReaderRedirect>
      }
    >
      <TechDocsAddons>
        <ReportIssue />
        <TierSelector />
        <VersionPicker />
        <RecentDocsTracker />
        <TechDocsViewTracker />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <UnlinkedEntraGroupPickerFieldExtension />
        <TeamInformationFieldsExtension />
        <GitHubRepoPickerFieldExtension />
        <RepositoryNameFieldsExtension />
        <DomainPickerFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/groups" element={<GroupsExplorerPage />} />
    <Route path="/releases" element={<ReleasesPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppInsightsBootstrap />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
